import type { AcceptedBet, BetType, IBetLimits, IBetSpots, IBetSpotValue } from '../store/slices/bets/types';
import type { ChipValueType } from '../store/slices/chips/types';
import { ChipTypes } from '../store/slices/chips/types';

import { isInteger, roundNumber } from './number';

export const getMinimalBet = (betLimits: IBetLimits | null): number => {
  // FIXME: cache me
  return betLimits ? Math.min(...Object.values(betLimits).map((betLimit) => betLimit.min)) : 0;
};

export const acceptedBetAlreadyExists = (acceptedBets: AcceptedBet[], acceptedBet: AcceptedBet): boolean => {
  return !!acceptedBets.find((bet) => bet.amount === acceptedBet.amount && bet.betCode === acceptedBet.betCode);
};

export const areBetsEqual = (bet1?: BetType, bet2?: BetType): boolean => {
  if (!bet1 || !bet2) {
    return false;
  }
  let result = true;
  const keys = new Set([...Object.keys(bet1.betSpots), ...Object.keys(bet2.betSpots)]);
  keys.forEach((key) => {
    const betSpotValue1 = bet1.betSpots[key]?.value ?? 0;
    const betSpotValue2 = bet2.betSpots[key]?.value ?? 0;
    if (betSpotValue1 !== betSpotValue2) {
      result = false;
    }
  });
  return result;
};

export const rawTotalBet = (betSpots: IBetSpots = {}): number =>
  Object.values(betSpots).reduce((sum, { value }) => sum + value, 0);

export const countTotalBet = (betSpots: IBetSpots, digits: number): number => {
  const totalBet = rawTotalBet(betSpots);
  return roundNumber(totalBet, digits);
};

export const isFreeBetPlaced = (betSpots: IBetSpots = {}): boolean =>
  Object.values(betSpots).some((bet) => bet.type === ChipTypes.FreeChip);

export const calculateNewBetValue = (
  currentValue: number,
  addedValue: number,
  limit: number,
  decimals: number
): number => {
  if (isInteger(currentValue) && isInteger(addedValue)) {
    return Math.min(currentValue + addedValue, limit);
  } else {
    // Scale-up non-integers to avoid floating point errors
    return Math.min(roundNumber(currentValue + addedValue, decimals), limit);
  }
};

// TODO: check this function - seems like it's not fully correct for all the cases and requirements we have
export const isBetAvailable = (
  currentBets: BetType,
  changedBets: BetType,
  decimals: number,
  betLimits: IBetLimits,
  tableLimit: number
): boolean => {
  let maxedOutCount = 0;

  const changedBetsBetSpots = changedBets.betSpots ?? {};
  const currentBetsBetSpots = currentBets.betSpots ?? {};
  const tillTableLimit = tableLimit - countTotalBet(currentBetsBetSpots, decimals);
  const aboutToPlace = Object.keys(changedBetsBetSpots).reduce((sum, betId) => {
    const currentValue = currentBetsBetSpots[betId]?.value ?? 0;
    const addedValue = changedBetsBetSpots[betId]?.value ?? 0;
    const betLimit = betLimits[betId]?.max ?? Infinity;
    const calculatedBetValue = calculateNewBetValue(currentValue, addedValue, betLimit, decimals);
    if (calculatedBetValue >= betLimit) {
      maxedOutCount++;
    }
    return sum + calculatedBetValue === currentValue ? 0 : calculatedBetValue;
  }, 0);

  const updatedBetCount = Object.values(changedBetsBetSpots).filter(({ value }) => value > 0).length;
  const multiBet = updatedBetCount > 1;

  return !(tillTableLimit < aboutToPlace && multiBet && maxedOutCount < updatedBetCount - 1);
};

export const getNewBet = (
  currentBets: BetType | undefined,
  changedBets: BetType,
  decimals: number,
  betLimits: IBetLimits,
  betList: number[],
  tableLimit: number,
  balance: number,
  freeChipValue: number,
  freeChipBalance: number
): BetType | null => {
  currentBets ??= emptyBets();

  if (!isBetAvailable(currentBets, changedBets, decimals, betLimits, tableLimit)) {
    return null;
  }

  const newBets = currentBets.betSpots ? { ...currentBets, betSpots: { ...currentBets.betSpots } } : emptyBets();

  let negativeBetSum = Object.values(changedBets.betSpots).reduce(
    (sum, { value }) => (value < 0 ? sum + value : sum),
    0
  );
  let tillTableLimit = tableLimit - countTotalBet(newBets.betSpots, decimals) - negativeBetSum;

  for (const betId of betList) {
    const currentValue = currentBets.betSpots?.[betId]?.value ?? 0;
    const valueToAdd = changedBets.betSpots?.[betId]?.value ?? 0;
    const chipType = changedBets.betSpots?.[betId]?.type ?? currentBets.betSpots?.[betId]?.type ?? ChipTypes.CasinoChip;

    if (valueToAdd > 0 && tillTableLimit <= 0) {
      return null;
    }

    const addedValue = Math.min(valueToAdd, tillTableLimit);
    const betLimit = betLimits[betId]?.max || Infinity;
    const calculatedBetValue = calculateNewBetValue(currentValue, addedValue, betLimit, decimals);

    newBets.betSpots[betId] = {
      ...changedBets.betSpots[betId],
      value: calculatedBetValue,
      type: chipType,
    };
    //Partial free chip values cannot be used to place bets; the bet amount must be a multiple of the free chip value for free bets
    if (chipType === ChipTypes.FreeChip) {
      const freeChipCount = Math.floor(calculatedBetValue / freeChipValue);
      newBets.betSpots[betId].count = freeChipCount;
      newBets.betSpots[betId].value = freeChipValue * freeChipCount;
      balance = freeChipBalance;
    }
    if (valueToAdd < 0) {
      negativeBetSum += Math.abs(valueToAdd);
    }
    tillTableLimit = tableLimit - countTotalBet(newBets.betSpots, decimals) - negativeBetSum;
  }

  const totalBet = countTotalBet(newBets.betSpots, decimals);
  const fitsInBalance = totalBet <= balance;
  const fitsInTableLimit = totalBet <= tableLimit;
  const betsEqual = areBetsEqual(newBets, currentBets);
  if (!betsEqual && fitsInBalance && fitsInTableLimit) {
    return newBets;
  }
  return null;
};

// TODO: should this be merged with calculateNewBetValue?
export const doubleBetValue = (currentValue: number, betLimit: number, decimals: number): number => {
  if (isInteger(currentValue)) {
    return Math.min(currentValue * 2, betLimit);
  } else {
    // Scale-up non-integers to avoid floating point errors
    return Math.min(roundNumber(currentValue * 2, decimals), betLimit);
  }
};

export const doubleBets = (
  balance: number,
  decimals: number,
  bet: BetType,
  betLimits: IBetLimits | null,
  totalTableLimit: number,
  totalBet: number,
  freeChipValue: number,
  freeChipBalance: number,
  valueMultiplier?: number
): BetType => {
  const isFreeChip = isFreeBetPlaced(bet.betSpots);
  if (isFreeChip) {
    balance = freeChipBalance;
  }
  balance -= totalBet;

  const newBets = emptyBets();

  for (const [betId, betSpot] of Object.entries(bet.betSpots ?? {})) {
    const betLimit = betLimits?.[betId]?.max ?? Infinity;
    const doubledValue = doubleBetValue(betSpot.value, betLimit, decimals);

    let costToDouble = doubledValue - betSpot.value;

    if (valueMultiplier) {
      costToDouble *= valueMultiplier;
    }
    totalBet += costToDouble;

    const canDouble = balance >= costToDouble && totalBet <= totalTableLimit;
    if (canDouble) {
      newBets.betSpots[betId] = {
        ...betSpot,
        ...getBetSpotObj(doubledValue, freeChipValue, betSpot.type),
      };
      balance -= costToDouble;
    } else {
      return bet;
    }
  }
  return newBets;
};

export const validateBets = (
  balance: number,
  decimals: number,
  totalBet: number,
  betSpotIds: number[],
  betSpotLimits: IBetLimits | null,
  bets: BetType | void,
  selectedChip: ChipValueType,
  freeChipBalance: number,
  valueMultiplier?: number
): BetType => {
  const validBet = emptyBets();
  const chipValue = selectedChip.value;
  const chipType = selectedChip.type;
  if (chipType === ChipTypes.FreeChip) {
    balance = freeChipBalance;
  }
  /**
   * Invalid input data cases
   *
   *  bets without total bet or vice versa
   *  no balance
   *  no chip value
   */
  if ((totalBet && !bets) || !balance || !chipValue) {
    return validBet;
  }
  if (isMixedBets(chipType, bets)) {
    return validBet;
  }

  let subTotalBet = chipValue,
    betSpotValue = chipValue,
    blockedByLimit = false;

  betSpotIds.forEach((betSpot) => {
    blockedByLimit = false;

    const betLimit = betSpotLimits?.[betSpot]?.max || Infinity, // TODO: do we want to set Infinity in case of 0?
      currentBet = bets?.betSpots[betSpot]?.value ?? 0,
      maxAllowedBetValueForBetSpot = roundNumber(betLimit - currentBet, decimals);
    if (selectedChip.type === ChipTypes.FreeChip && maxAllowedBetValueForBetSpot < chipValue) {
      return validBet;
    }
    betSpotValue = Math.min(chipValue, maxAllowedBetValueForBetSpot);
    subTotalBet = valueMultiplier ? roundNumber(betSpotValue * valueMultiplier, decimals) : betSpotValue;
    blockedByLimit = betLimit <= currentBet;

    if (balance >= roundNumber(subTotalBet + totalBet, decimals) && !blockedByLimit) {
      balance = roundNumber(balance - subTotalBet, decimals);
      validBet.betSpots[betSpot] = getBetSpotObj(betSpotValue, chipValue, chipType);
    }
  });
  return validBet;
};

const getBetSpotObj = (
  betSpotValue: number,
  chipValue: number,
  chipType: ChipTypes = ChipTypes.CasinoChip
): IBetSpotValue => {
  const betObj: IBetSpotValue = { value: betSpotValue, type: chipType };
  if (chipType === ChipTypes.FreeChip) {
    betObj.count = Math.floor(betSpotValue / chipValue);
    betObj.value = betObj.count * chipValue;
  }
  return betObj;
};

export const emptyBets = (): BetType => ({ betSpots: {} });

export const getLastBetType = (lastBets: BetType | null): ChipTypes => {
  if (!lastBets?.betSpots) {
    return ChipTypes.CasinoChip;
  }
  const betSpot = Object.values(lastBets.betSpots).find((betSpot) => !!betSpot.value);
  return betSpot?.type || ChipTypes.CasinoChip;
};

export const getChipTypeOnBetSpot = (bets: BetType | undefined, betSpot: any): ChipTypes => {
  return bets?.betSpots[betSpot]?.type || ChipTypes.CasinoChip;
};

export const isMixedBets = (selectedChipType: ChipTypes, bets: BetType | void): boolean => {
  if (!bets) {
    return false;
  }
  const betObj = Object.values(bets.betSpots).find((betSpot) => betSpot.value > 0);
  return Boolean(betObj && selectedChipType !== betObj.type);
};
